import { useRef, useState } from 'react'
import {
  confirmForcedReturn,
  pendingJobsFromError,
  isForcedReturnRequiredError
} from './forcedReturnGate'

const EMPTY_DIALOG = { open: false, jobs: [], changed: false, submitting: false, fabricationOrderId: '' }

/**
 * S119-S122 (CU6/RN44, historia #100) — orchestrates the forced-return gate around any cancel/close
 * call of a fabrication order. `runGuarded(runAction, fabricationOrderId)` runs `runAction` (the
 * real apiDelete/apiPost of the cancel/close request); if Sun answers "requires confirmation"
 * (E-M13), the returned promise is deliberately left pending — same structural pattern as historia
 * #49's `saveFabricationOrderWithDeliveryValidation` ("the outcome is fully owned by the dialog, not
 * by the table's own generic success/error handling") — until the user decides:
 *  - `onCancel()`: nothing is sent, nothing proceeds (S120); the guarded promise never settles.
 *  - `onConfirm(decisions)`: posts the per-job decisions; on success, RETRIES `runAction` (S121 —
 *    "the cancellation or close continues with the same confirmation and warnings as today") and
 *    settles the guarded promise with its outcome; on a fresh "requires confirmation" (RN44.2/V15,
 *    the set of dispatched jobs changed since the dialog was shown), reopens the dialog with the NEW
 *    set instead of settling — the previous decisions are discarded, never applied to the new set.
 *
 * `fabricationOrderId` is supplied by the CALLER, not read off the error: Sun's structured 409
 * (convergence, lote 17) carries only `{ message, pendingJobs }` -- the caller already knows which
 * fabrication order it is cancelling/closing (FabricationOrder.jsx's own cancel/close handlers),
 * and that value is what `onConfirm` later sends back to `forcedReturn/confirm`.
 */
export const useForcedReturnGate = () => {
  const [dialog, setDialog] = useState(EMPTY_DIALOG)
  const pendingRef = useRef(null)

  const openWith = (error, changed, fabricationOrderId) => {
    setDialog({
      open: true,
      jobs: pendingJobsFromError(error),
      changed,
      submitting: false,
      fabricationOrderId
    })
  }

  const runGuarded = (runAction, fabricationOrderId = '') => runAction().catch((error) => {
    if (!isForcedReturnRequiredError(error)) throw error
    return new Promise((resolve, reject) => {
      pendingRef.current = { runAction, resolve, reject }
      openWith(error, false, fabricationOrderId)
    })
  })

  const onCancel = () => {
    // S120: the operation does not proceed — the guarded promise is left pending forever, mirroring
    // #49's own "left pending on cancel" contract.
    pendingRef.current = null
    setDialog(EMPTY_DIALOG)
  }

  const onConfirm = async (choices) => {
    const pending = pendingRef.current
    if (!pending) return
    setDialog((previous) => ({ ...previous, submitting: true }))
    try {
      await confirmForcedReturn(dialog.fabricationOrderId, dialog.jobs, choices)
      setDialog(EMPTY_DIALOG)
      try {
        const result = await pending.runAction()
        pendingRef.current = null
        pending.resolve(result)
      } catch (retryError) {
        pendingRef.current = null
        pending.reject(retryError)
      }
    } catch (error) {
      if (isForcedReturnRequiredError(error)) {
        // RN44.2/V15: re-validation found the set changed — reopen with the fresh set instead of
        // ever applying a decision the user did not see the final set for, on the SAME fabrication
        // order (it never came from the error to begin with, so there is nothing to re-derive).
        openWith(error, true, dialog.fabricationOrderId)
      } else {
        pendingRef.current = null
        setDialog(EMPTY_DIALOG)
        pending.reject(error)
      }
    }
  }

  return { dialog, runGuarded, onCancel, onConfirm }
}
